/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "cc-cookie-consent";

type ConsentStatus = "granted" | "denied" | "pending";

let listeners: Array<() => void> = [];

function emitChange() {
  for (const listener of listeners) {
    listener();
  }
}

function getSnapshot(): ConsentStatus {
  if (typeof window === "undefined") return "pending";
  return (localStorage.getItem(STORAGE_KEY) as ConsentStatus) ?? "pending";
}

function getServerSnapshot(): ConsentStatus {
  return "pending";
}

function subscribe(listener: () => void): () => void {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

export function useCookieConsent() {
  const status = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const grant = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "granted");
    emitChange();
  }, []);

  const deny = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "denied");
    emitChange();
  }, []);

  return {
    status,
    isGranted: status === "granted",
    isPending: status === "pending",
    grant,
    deny,
  };
}

/** Non-hook check for use outside React (e.g., in PostHog init) */
export function getCookieConsent(): ConsentStatus {
  return getSnapshot();
}
